import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCalendarAlt, FaCheck, FaTimes, FaUtensils, FaBed } from 'react-icons/fa';
import { packagesData } from '../../data/packages';
import './PackageDetailPage.css';

const PackageDetailPage = () => {
  const { id } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const relatedPackages = Object.values(packagesData).filter(
    (pkg) => pkg.id !== packageData?.id
  );

  useEffect(() => {
    if (packagesData[id]) {
      setPackageData(packagesData[id]);
    }
    setLoading(false);
  }, [id]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!packageData) {
    return (
      <div className="container not-found">
        <h2>Package Not Found</h2>
        <p>Sorry, the package you're looking for doesn't exist.</p>
        <Link to="/packages" className="btn btn-primary">Back to Packages</Link>
      </div>
    );
  }

  return (
    <div className="package-detail-page">
      <div className="package-hero" style={{ backgroundImage: `url(${packageData.heroImage})` }}>
        <div className="package-hero-overlay"></div>
        <div className="container">
          <div className="package-hero-content">
            <h1>{packageData.title}</h1>
            <p className="package-tagline">{packageData.tagline}</p>
            <div className="package-meta">
              <div className="package-meta-item">
                <FaCalendarAlt />
                <span>{packageData.days} Days</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="package-content">
        <div className="container">
          <section className="package-section">
            <div className="section-header">
              <h2>Overview</h2>
            </div>
            <p>{packageData.overview}</p>

            <div className="highlights-container">
              <h3>Package Highlights</h3>
              <ul className="highlights-list">
                {packageData.highlights.map((highlight, index) => (
                  <li key={index}><FaCheck className="highlight-icon" /> {highlight}</li>
                ))}
              </ul>
            </div>
          </section>

          <section className="package-section">
            <div className="section-header">
              <h2>Detailed Itinerary</h2>
            </div>
            <div className="itinerary-timeline">
              {packageData.itinerary.map((day) => (
                <div className="itinerary-day" key={day.day}>
                  <div className="itinerary-day-number">Day {day.day}</div>
                  <div className="itinerary-day-content">
                    <h3>{day.title}</h3>
                    <p>{day.description}</p>
                    <div className="itinerary-day-details">
                      <div className="itinerary-detail">
                        <FaBed />
                        <span><strong>Accommodation:</strong> {day.accommodation}</span>
                      </div>
                      <div className="itinerary-detail">
                        <FaUtensils />
                        <span><strong>Meals:</strong> {day.meals}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {packageData.gallery?.length > 0 && (
            <section className="package-section package-gallery-section">
              <div className="section-header">
                <h2>Our Homestay</h2>
                <p>Take a look at our homestay — AC rooms, dining area, lounge, and outdoor spaces</p>
              </div>
              <div className="package-gallery-grid">
                {packageData.gallery.map((image) => (
                  <div className="package-gallery-item" key={image}>
                    <img src={image} alt="Alleppey homestay" loading="lazy" />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="package-section">
            <div className="inclusions-exclusions">
              <div className="inclusions">
                <h3>What's Included</h3>
                <ul>
                  {packageData.inclusions.map((item, index) => (
                    <li key={index}><FaCheck className="included-icon" /> {item}</li>
                  ))}
                </ul>
              </div>
              <div className="exclusions">
                <h3>What's Not Included</h3>
                <ul>
                  {packageData.exclusions.map((item, index) => (
                    <li key={index}><FaTimes className="excluded-icon" /> {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="package-section">
            <div className="additional-info">
              <div className="info-card">
                <h3>Best Time to Visit</h3>
                <p><FaCalendarAlt className="info-icon" /> {packageData.bestTimeToVisit}</p>
              </div>
            </div>
            {packageData.royalCruiseAddOn && (
              <div className="package-notes">
                <h3>Royal Cruise Add-On</h3>
                <p>{packageData.royalCruiseAddOn}</p>
              </div>
            )}
            <div className="package-notes">
              <h3>Notes</h3>
              <p>{packageData.notes}</p>
            </div>
          </section>

          <section className="package-cta">
            <h2>Ready to Experience This Package?</h2>
            <p>Contact us to customize this package according to your preferences or to make a booking.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">Book This Package</Link>
              <Link to="/contact" className="btn btn-secondary">Customize This Package</Link>
            </div>
          </section>

          {relatedPackages.length > 0 && (
            <section className="package-section">
              <div className="section-header">
                <h2>You May Also Like</h2>
              </div>
              <div className="related-packages">
                {relatedPackages.map((pkg) => (
                  <div className="related-package-card" key={pkg.id}>
                    <div className="related-package-image">
                      <img src={pkg.heroImage} alt={pkg.title} />
                      <div className="related-package-days">
                        <FaCalendarAlt />
                        <span>{pkg.days} Days</span>
                      </div>
                    </div>
                    <div className="related-package-content">
                      <h3>{pkg.title}</h3>
                      <Link to={`/packages/${pkg.id}`} className="btn btn-primary">View Details</Link>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageDetailPage;
